import React, { useState } from "react";
import { Table, Row, Col, Form, Button, Pagination } from "react-bootstrap";
import {
  BarChart3,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { CiSearch } from "react-icons/ci";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { LuDownload } from "react-icons/lu";
import Chart from "../Charts/Chart";
import FASChart from "../Charts/FASChart";
import { useTheme } from "../../Themecontext/Themecontext";
import { FaPercent } from "react-icons/fa6";

const ColourTable = ({
  columns = [],
  data = [],
  apiTotals = null,
  externalTotals = {},
}) => {
  const { Colourdata } = useSelector((state) => state.ClientLogin);

  const iscolour = Colourdata?.data?.find(
    (el) => el.ExeName === "NEWCOLOURCHEMICL",
  );
  const iscolourDark = Colourdata?.data?.find(
    (el) => el.ExeName === "NEWCOLOURCHEMICLDARK",
  );
  const { theme } = useTheme();
  const isdark = theme;

  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [showPercent, setShowPercent] = useState(false);
  const rowsPerPage = 10;

  const [selectedRow, setSelectedRow] = useState(null);

  const labelMap = columns.reduce((acc, col) => {
    acc[col.key] = col.label;
    return acc;
  }, {});

  /* SEARCH */

  const filteredData = data.filter((row) =>
    Object.values(row).some((val) =>
      val?.toString().toLowerCase().includes(search.trim().toLowerCase()),
    ),
  );

  /* NUMBER FORMAT */

  const formatIndianNumber = (num) => {
    const number = Number(num);
    if (isNaN(number)) return num;

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatRupees = (num) => {
    if (num === null || num === undefined || num === "") return "0";

    const number = parseFloat(num);

    if (isNaN(number)) return "0";

    return number.toLocaleString("en-IN"); // 👈 no decimal
  };

  /* SORTING */

  const handleSort = (key) => {
    let direction = "desc";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "desc") {
        direction = "asc";
      } else if (sortConfig.direction === "asc") {
        setSortConfig({
          key: null,
          direction: null,
        });
        return;
      }
    }

    setSortConfig({ key, direction });
  };
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    const cleanA = String(aValue).replace(/,/g, "");
    const cleanB = String(bValue).replace(/,/g, "");

    if (!isNaN(cleanA) && !isNaN(cleanB)) {
      aValue = Number(cleanA);
      bValue = Number(cleanB);
    }

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;

    return 0;
  });

  /* PAGINATION */

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  /* VISIBLE PAGE CONTROL */

  const visiblePages = 4;

  let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
  let endPage = startPage + visiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - visiblePages + 1);
  }

  const cleanNumber = (val) => {
    if (val === null || val === undefined) return 0;

    if (typeof val === "number") return val;

    const cleaned = String(val).replace(/[^\d.-]/g, "");
    const parsed = parseFloat(cleaned);

    return isNaN(parsed) ? 0 : parsed;
  };

  // const isNumericValue = (val) => {
  //   if (val === null || val === undefined || val === "") return false;

  //   const cleaned = String(val).replace(/[^\d.-]/g, "");

  //   return cleaned !== "" && !isNaN(cleaned);
  // };

  const isNumericValue = (val) => {
    if (val === null || val === undefined) return false;

    const num = Number(
      typeof val === "string" ? val.replace(/,/g, "") : val
    );

    return !isNaN(num);
  };


  const calculateColumnTotal = (columnAccessor) => {
    return sortedData.reduce((sum, row) => {
      let value = row[columnAccessor];
      if (typeof value === "string")
        value = parseFloat(value.replace(/,/g, ""));
      return sum + (typeof value === "number" && !isNaN(value) ? value : 0);
    }, 0);
  };

  const getColumnTotal = (columnAccessor) => {
    // Agar parent se total aaya hai → use karo
    if (externalTotals && externalTotals[columnAccessor] !== undefined) {
      return externalTotals[columnAccessor];
    }

    return calculateColumnTotal(columnAccessor);
  };

  const downloadCSV = () => {
    if (!sortedData || sortedData.length === 0) return;

    // headers
    const headers = columns.map((col) => col.label);

    // rows
    const rows = sortedData.map((row) =>
      columns.map((col) => {
        let value = row[col.key];

        // commas handle karne ke liye quotes
        if (typeof value === "string" && value.includes(",")) {
          value = `"${value}"`;
        }

        return value ?? "";
      }),
    );

    // combine
    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");

    // file create
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    // download trigger
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "table_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <style>
        {`
    .custom-pagination {
      gap: 8px;
    }

    .custom-pagination .page-item .page-link {
      border: none;
      border-radius: 8px;
      min-width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour6};
      color: ${isdark === "light" ? iscolour?.Colour8 : "#fff"};
      font-weight: 500;
    }

    .custom-pagination .page-item.active .page-link {
      background: ${isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour9}; 
 color: ${isdark === "light" ? iscolour?.Colour8 : iscolourDark?.Colour12};

    }

    .custom-pagination .page-item .page-link:hover {
      background: ${isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour9};   
 color: ${isdark === "light" ? iscolour?.Colour8 : iscolourDark?.Colour12};
    }




    
       .search-input::placeholder {
                                    color: ${isdark === "light" ? iscolour?.Colour6 : iscolourDark?.Colour9};
                                    opacity: 1;
                                    }
                                    .search-input:focus{
  border-color: ${isdark === "light"
            ? iscolour?.Colour1
            : iscolourDark?.Colour1} !important;

  box-shadow: 0 0 0 0.2rem ${isdark === "light"
            ? `${iscolour?.Colour6}40`
            : `${iscolourDark?.Colour8}40`
          } !important;

  outline: none !important;
}
  `}
      </style>

      <div
        style={{
          width: "100%",
          background:
            isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour5,
        }}
      >
        {/* TOP BAR */}
        <Row className="g-2 align-items-center mb-2 m-0 px-2 px-md-3">
          {/* Search */}
          <Col xs={12} md={5} lg={5} xl={4} >
            <div
              style={{
                position: "relative",
                width: "100%",
              }}
            >
              <CiSearch
                size={20}
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color:
                    isdark === "light"
                      ? iscolour?.Colour6
                      : iscolourDark?.Colour9,
                  zIndex: 1,
                }}
              />



              <Form.Control
                type="text"
                placeholder="Search..."
                className="search-input"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  borderRadius: "8px",
                  height: "40px",
                  padding: "0 10px 0 35px",
                  border: `1px solid ${isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour6}`,
                  backgroundColor: isdark === "light" ? "#fff" : iscolourDark?.Colour4,
                  color: isdark === "light" ? "#000" : "#fff",
                }}
              />
            </div>
          </Col>

          {/* Buttons */}
          <Col
            xs={12} md={7} lg={7} xl={8} style={{ textAlign: "right" }}

          >
            <Button
              className="hover-scale-btn me-2"
              onClick={() => setShowPercent(!showPercent)}
              style={{
                background:
                  isdark === "light"
                    ? iscolour?.Colour6
                    : iscolourDark?.Colour6,
                border: "none",
                borderRadius: "8px",
                height: "40px",
                width: "40px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaPercent size={16} />
            </Button>

            <Button
              onClick={downloadCSV}
              className=" hover-scale-btn"
              style={{
                background:
                  isdark === "light"
                    ? iscolour?.Colour6
                    : iscolourDark?.Colour6,
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600"
              }}
            >
              <LuDownload size={16} />
              <span className=" ms-1">
                Download CSV
              </span>
            </Button>
          </Col>

        </Row>

        {/* TABLE */}
        <div
          className="grey-scroll"
          style={{
            overflowX: currentRows.length > 0 ? "auto" : "hidden",
            overflowY: "auto",
            width: "100%",
            height: "400px",
            position: "relative",
            scrollbarWidth: "thin",
            scrollbarColor:
              isdark === "light"
                ? `${iscolour?.Colour7} ${iscolour?.Colour5}`
                : `${iscolourDark?.Colour7} ${iscolourDark?.Colour3}`,
          }}
        >
          {currentRows.length > 0 ? (
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                minWidth: "900px",
              }}
            >
              <thead>
                <tr>
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      onClick={() => handleSort(col.key)}
                      style={{
                        width: col.width || "auto",
                        minWidth: col.width || "auto",
                        maxWidth: col.width || "auto",
                        padding: "15px",
                        fontWeight: "700",
                        fontSize: "13px",
                        textAlign: "center",
                        cursor: "pointer",
                        verticalAlign: "middle",
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                        background:
                          isdark === "light"
                            ? iscolour?.Colour3
                            : iscolourDark?.Colour5,
                        color: isdark === "light" ? "#000" : "#fff",
                        zIndex: 1,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                        }}
                      >
                        {col.label}

                        {sortConfig.key === col.key ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp size={14} />
                          ) : (
                            <ArrowDown size={14} />
                          )
                        ) : (
                          <ArrowUpDown size={14} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {currentRows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    style={{
                      backgroundColor:
                        rowIndex % 2 === 0
                          ? isdark === "light"
                            ? iscolour?.Colour1
                            : iscolourDark?.Colour1
                          : isdark === "light"
                            ? iscolour?.Colour4
                            : iscolourDark?.Colour3,
                    }}
                  >
                    {columns.map((col, colIndex) => {
                      const value =
                        col.key === "srNumber"
                          ? row.srNumber !== undefined && row.srNumber !== null
                            ? row.srNumber
                            : indexOfFirstRow + rowIndex + 1
                          : row[col.key];

                      return (
                        <td
                          key={colIndex}
                          style={{
                            padding: "15px",
                            textAlign: col.align || "left",
                            fontSize: "14px",
                            fontWeight: "700",
                            verticalAlign: "middle",
                            width: col.width || "auto",
                            minWidth: col.width || "auto",
                            maxWidth: col.width || "auto",
                            whiteSpace: col.wrap ? "normal" : "nowrap",
                            wordBreak: col.wrap ? "break-word" : "normal",
                            overflowWrap: col.wrap ? "break-word" : "normal",
                            overflow: "hidden",
                            textOverflow: col.wrap ? "unset" : "ellipsis",

                            color:
                              col.colors ||
                              (isdark === "light"
                                ? iscolour?.Colour7
                                : iscolourDark?.Colour9),
                          }}
                        >
                          {col.key === "charts" ? (
                            <div
                              onClick={() => setSelectedRow(row)}
                              style={{
                                background:
                                  isdark === "light"
                                    ? iscolour?.Colour3
                                    : iscolourDark?.Colour5,
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "8px",
                                color:
                                  isdark === "light"
                                    ? iscolour?.Colour9
                                    : iscolourDark?.Colour12,
                                margin: "auto",
                                cursor: "pointer",
                              }}
                            >
                              <BarChart3 size={16} />
                            </div>
                          ) : (() => {
                            const skipPercentageFor = [
                              "srNumber",

                              "Acname",
                              "InwNo",
                              "BillDate",
                              // "Qty",
                              "LstDate",
                              // "LstRate",
                              "LstQty",
                              // "DiffRate",
                              "mxDate",
                              // "mxRate",
                              "mnDate",
                              // "mnRate",
                              "AvgRate",
                              "ClosingStk",
                              "AvQtyCons",
                              "itemcount",
                              "TodayPQtyCost",
                              "UptoMeter",
                              "UptoCostAmount",
                              "UptoPMeterCost",
                              "ItemName",
                              "itemname",
                              "NoOfBatch",
                              "srNo"
                            ];

                            const isNumeric =
                              !skipPercentageFor.includes(col.key) &&
                              isNumericValue(value);

                            if (isNumeric) {
                              const numericValue = cleanNumber(value);

                              const total = calculateColumnTotal(col.key);

                              const percentage =
                                total !== 0
                                  ? ((numericValue / total) * 100).toFixed(2)
                                  : "0.00";

                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems:
                                      col.align === "center"
                                        ? "center"
                                        : col.align === "right"
                                          ? "flex-end"
                                          : "flex-start",
                                  }}
                                >
                                  <div>
                                    {col.rupees
                                      ? formatRupees(numericValue)
                                      : col.format
                                        ? formatIndianNumber(numericValue)
                                        : numericValue}
                                  </div>

                                  {showPercent && (
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color:
                                          isdark === "light"
                                            ? "#666"
                                            : iscolourDark?.Colour4,
                                      }}
                                    >
                                      ({percentage}%)
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return col.rupees
                              ? formatRupees(value)
                              : col.format
                                ? formatIndianNumber(value)
                                : value;
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr
                  style={{

                    fontWeight: "bold",
                    position: "sticky",
                    bottom: 0,
                    zIndex: 2,
                  }}
                >
                  {columns.map((col, i) => {
                    let value = "";
                    const dashColumns = [
                      "srNumber",
                      "BillDate",
                      "Acname",
                      "ItemName",
                      "UnitName",
                      "Qty",
                      "Rate",
                      "LstRate",
                      "mnDate",
                      "mxDate",
                      "LstDate",
                      "LstQty",
                      "DiffRate",
                      "mxRate",
                      "mnRate",
                      "AvgRate",
                      "ClosingStk",
                      "AvQtyCons",
                      "DaysOfStock",
                      "TodayPQtyCost",
                      "itemcount",
                      "UptoMeter",
                      "NoOfBatch",
                      "UptoCostAmount",
                      "UptoPMeterCost",
                      "class",
                      "itemname",
                      "ItemGroupName"
                    ];

                    const totalKey = col.totalKey || col.key;

                    if (i === 1) {
                      value = "Total";
                    } else if (dashColumns.includes(col.key)) {
                      value = "-";
                    } else if (col.key === "srNumber") {
                      value = sortedData.length;
                    } else {
                      const totalValue =
                        externalTotals && externalTotals[totalKey] !== undefined
                          ? externalTotals[totalKey]
                          : calculateColumnTotal(col.key);

                      value = col.rupees
                        ? formatRupees(totalValue)
                        : col.format
                          ? formatIndianNumber(totalValue)
                          : totalValue;
                    }

                    return (
                      <td
                        key={i}
                        style={{
                          padding: "14px",
                          fontSize: "15px",
                          textAlign: col.align || "left",
                          background:
                            isdark === "light"
                              ? iscolour?.Colour7
                              : iscolourDark?.Colour5,
                          color:
                            isdark === "light"
                              ? iscolour?.Colour1
                              : iscolourDark?.Colour9,
                        }}
                      >
                        {(() => {
                          if (value === "-") return "-";

                          const skipPercentageFor = [
                            "srNumber",
                            "BillDate",
                            "Acname",
                            "ItemName",
                            "UnitName",
                            "Qty",
                            "Rate",
                            "LstRate",
                            "mnDate",
                            "mxDate",
                            "LstDate",
                            "LstQty",
                            "DiffRate",
                            "mxRate",
                            "mnRate",
                            "AvgRate",
                            "ClosingStk",
                            "AvQtyCons",
                            "DaysOfStock",
                            "TodayPQtyCost",
                            "itemcount",
                            "UptoMeter",
                            "NoOfBatch",
                            "UptoCostAmount",
                            "UptoPMeterCost",
                            "class",
                            "itemname",
                          ];

                          const isNumeric =
                            !skipPercentageFor.includes(col.key) &&
                            isNumericValue(value);

                          if (isNumeric) {
                            const numericValue = cleanNumber(value);

                            return (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems:
                                    col.align === "center"
                                      ? "center"
                                      : col.align === "right"
                                        ? "flex-end"
                                        : "flex-start",
                                }}
                              >
                                <div>
                                  {col.rupees
                                    ? formatRupees(numericValue)
                                    : col.format
                                      ? formatIndianNumber(numericValue)
                                      : numericValue}
                                </div>

                                {showPercent && (
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color:
                                        isdark === "light"
                                          ? "#f5f5f5"
                                          : iscolourDark?.Colour12,
                                    }}
                                  >
                                    (100%)
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return col.rupees
                            ? formatRupees(value)
                            : col.format
                              ? formatIndianNumber(value)
                              : value;
                        })()}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  isdark === "light"
                    ? iscolour?.Colour3
                    : iscolourDark?.Colour5,
                overflow: "hidden",
                padding: "20px",
              }}
            >
              {/* BACKGROUND CIRCLE 1 */}
              <div
                style={{
                  position: "absolute",
                  width: "clamp(150px, 25vw, 250px)",
                  height: "clamp(150px, 25vw, 250px)",
                  borderRadius: "50%",
                  background:
                    isdark === "light"
                      ? "rgba(0,0,0,0.03)"
                      : "rgba(255,255,255,0.03)",
                  top: "-60px",
                  right: "-60px",
                }}
              />

              {/* BACKGROUND CIRCLE 2 */}
              <div
                style={{
                  position: "absolute",
                  width: "clamp(120px, 20vw, 180px)",
                  height: "clamp(120px, 20vw, 180px)",
                  borderRadius: "50%",
                  background:
                    isdark === "light"
                      ? "rgba(0,0,0,0.03)"
                      : "rgba(255,255,255,0.03)",
                  bottom: "-40px",
                  left: "-40px",
                }}
              />

              {/* PREMIUM EMPTY STATE CARD */}
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  maxWidth: "450px",
                  padding: "40px",
                  borderRadius: "24px",
                  background: isdark === "light" 
                    ? "rgba(255, 255, 255, 0.6)" 
                    : "rgba(30, 30, 35, 0.6)",
                  boxShadow: isdark === "light" 
                    ? "0 8px 32px rgba(0, 0, 0, 0.05)" 
                    : "0 8px 32px rgba(0, 0, 0, 0.3)",
                  backdropFilter: "blur(12px)",
                  border: isdark === "light" 
                    ? "1px solid rgba(255,255,255,0.8)" 
                    : "1px solid rgba(255,255,255,0.05)",
                  textAlign: "center",
                  margin: "20px auto",
                }}
              >
                {/* ICON CONTAINER WITH GLOW */}
                <div
                  style={{
                    width: "90px",
                    height: "90px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "24px",
                    background: isdark === "light" 
                      ? `${iscolour?.Colour11 || "#1e293b"}1A`
                      : `${iscolourDark?.Colour12 || "#f1f5f9"}26`,
                    boxShadow: isdark === "light" 
                      ? `0 10px 25px -5px ${iscolour?.Colour11 || "#1e293b"}33`
                      : `0 10px 25px -5px ${iscolourDark?.Colour12 || "#f1f5f9"}4D`,
                    color: isdark === "light" ? "#1e293b" : "#f1f5f9",
                  }}
                >
                  {search ? (
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.3-4.3"/>
                      <path d="m9 9 4 4"/>
                      <path d="m13 9-4 4"/>
                    </svg>
                  ) : (
                    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
                      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
                    </svg>
                  )}
                </div>

                <h2
                  style={{
                    margin: "0 0 12px 0",
                    fontWeight: "700",
                    fontSize: "22px",
                    color: isdark === "light" ? "#1e293b" : "#f1f5f9",
                    letterSpacing: "-0.5px"
                  }}
                >
                  {search ? (
                    <span>
                      No results for <span style={{ color: isdark === "light" ? "#1e293b" : "#f1f5f9" }}>"{search}"</span>
                    </span>
                  ) : (
                    "No Data Available"
                  )}
                </h2>

                <p
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    lineHeight: "1.6",
                    color: isdark === "light" ? "#64748b" : "#94a3b8",
                  }}
                >
                  {search
                    ? "We couldn't find anything matching your search. Please try a different term or clear the filter."
                    : "There are currently no records to display."
                  }
                </p>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        {currentRows.length > 0 && (
          <div style={{ margin: "0px 10px" }}>
            <Row
              style={{
                backgroundColor:
                  isdark === "light" ? "#fff" : iscolourDark?.Colour7,
                padding: "10px",
              }}
            >
              <Col
                sm={12}
                md={6}
                lg={6}
                className="text-center text-md-start p-0 pb-md-2"
                style={{
                  fontSize: "13px",
                  color: isdark === "light" ? "#000" : (iscolourDark?.Colour1 || "#fff")
                }}
              >
                <div>
                  Showing {indexOfFirstRow + 1} to{" "}
                  {Math.min(indexOfLastRow, sortedData.length)} of{" "}
                  {sortedData.length} results
                </div>
              </Col>

              <Col
                sm={12}
                md={6}
                lg={6}
                className="d-flex justify-content-center p-0 justify-content-md-end"

              >
                <Pagination className="custom-pagination">
                  {/* FIRST PAGE */}
                  <Pagination.First
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  />

                  {/* PREVIOUS */}
                  <Pagination.Prev
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  />

                  {/* PAGE NUMBERS */}
                  {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                    const page = startPage + i;

                    return (
                      <Pagination.Item
                        key={page}
                        active={currentPage === page}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}

                  {/* NEXT */}
                  <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  />

                  {/* LAST PAGE */}
                  <Pagination.Last
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  />
                </Pagination>
              </Col>
            </Row>
          </div>
        )}
      </div>

      {selectedRow && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setSelectedRow(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "90%",
              maxWidth: "1100px",
              background:
                isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour7,
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
              overflow: "hidden",
            }}
            className="p-2 p-sm-3"
          >
            <Row>
              <Col xs={12} className="d-flex justify-content-end mb-3">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setSelectedRow(null)}
                >
                  Close
                </Button>
              </Col>

              <Col xs={12} className="p-0">
                <FASChart data={selectedRow} labelMap={labelMap} />
              </Col>
            </Row>
          </div>
        </div>
      )}
    </>
  );
};

export default ColourTable;
